/**
 * One answer on the check screen, as the Homework board draws it (#648): a full-width tinted row with an EMPTY round
 * marker and the answer's words. It is a button that acts when pressed, exactly as the pill it replaces did: nothing is
 * ever pre-selected, because a pre-selected reading would invite a confirmation the learner never gave, and a false
 * accept is the asymmetric failure. The marker is drawn filled only for a row that is open (`expanded`), which is the
 * learner's own click.
 *
 * A row that has just appeared ignores activation for a moment (`answer-lock.tsx`, #660), so a tap that moved the check on
 * cannot also answer the next line. Nothing about it is drawn.
 */

import { useAnswerGuard } from "./answer-lock.js";

export interface ChoiceRowProps {
  readonly onClick: () => void;
  readonly disabled?: boolean;
  /** For the row that opens something (the text box): whether it is open. */
  readonly expanded?: boolean;
  readonly children: React.ReactNode;
}

export function ChoiceRow({ onClick, disabled = false, expanded, children }: ChoiceRowProps): React.JSX.Element {
  const allows = useAnswerGuard();
  return (
    <button
      type="button"
      className="choice-row"
      disabled={disabled}
      aria-expanded={expanded}
      onClick={(event) => {
        if (allows(event)) onClick();
      }}
    >
      <span className="choice-marker" aria-hidden="true" data-on={expanded === true ? 'true' : undefined} />
      <span className="choice-body">{children}</span>
    </button>
  );
}
